import dynamic from 'next/dynamic';
import { oneLine } from 'common-tags';

import { BackgroundImage, Container, RichInline } from '@/atoms';
import { TwoColumn } from '@/molecules';

import { eyebrowClass, sectionBgProps, textStyleClass } from '@/utils';
import { getRealUrl } from '@/utils/utils';

const ContactForm = dynamic(() => import('@/molecules/contact-form'), {
  ssr: true,
});

/**
 * ComponentRevisadasContactForm
 *
 * Eyebrow + Titulo (richtext, soporta `<mark>` para el highlight de marca) +
 * Descripcion + Image. En desktop, Image es la columna izquierda y el
 * formulario va a la derecha. En mobile, Image es un banner superior de
 * 180px y el formulario queda debajo.
 */
export const ComponentRevisadasContactForm = ({
  Eyebrow,
  EyebrowEstilo,
  Titulo,
  Descripcion,
  TituloEstilo,
  DescripcionEstilo,
  Image,
  FormStyle,
  FondoSeccion,
  // Rediseño HoM opt-in: `hom` activa el scope de inputs tenant
  // (`contact-form-scope`: bordes --inputborder, labels bold, link de
  // privacidad oscuro) y la cabecera Eyebrow+RichInline alineada a la
  // derecha. Default = render legacy (h1/h3 pelados, sin scope).
  //
  // En la query GraphQL el campo viaja aliasado como `EstiloContactForm`
  // (los enums de Strapi son un tipo por componente y dos `Estilo` en la
  // misma dynamic zone no se pueden mergear); se acepta cualquiera de los
  // dos nombres para no depender de que cada página aplique transformer.
  Estilo,
  EstiloContactForm,
  Destinatario,
}: any) => {
  const isHom = (EstiloContactForm ?? Estilo) === 'hom';
  const eyebrowCls = eyebrowClass(EyebrowEstilo);
  const tituloCls = textStyleClass(TituloEstilo);
  const descripcionCls = textStyleClass(DescripcionEstilo);
  const formScope = isHom ? 'contact-form-scope' : '';

  const homHeader = (Eyebrow || Titulo) && (
    <div className="flex flex-col items-end self-start">
      {Eyebrow && (
        <RichInline
          html={Eyebrow}
          className={oneLine`font-accent text-primary-500 ${
            eyebrowCls ?? 'text-3xl leading-10 font-normal'
          }`}
        />
      )}
      {Titulo && (
        <RichInline
          as="h1"
          html={Titulo}
          className={oneLine`text-headline text-text-primary font-primary ${
            tituloCls ?? 'text-headline-h1 font-bold'
          }`}
        />
      )}
    </div>
  );
  // Legacy: h1 en la variante con imagen, h3 en la variante sin imagen
  // (idéntico a e1f26a9), sin wrapper ni estilos por defecto.
  const header = isHom ? homHeader : <h1 className={tituloCls}>{Titulo}</h1>;
  const headerNoImage = isHom ? (
    homHeader
  ) : (
    <h3 className={tituloCls}>{Titulo}</h3>
  );

  // `FondoSeccion` se aplica a la sección externa para que ambas variantes
  // (con/sin Image) la respeten. En la variante con Image, la columna
  // izquierda queda cubierta por el `<BackgroundImage>` así que el color
  // sólo se ve alrededor del formulario.
  const sectionBg = sectionBgProps(FondoSeccion);

  return Image?.url ? (
    <section className="w-full" style={sectionBg.style}>
      <TwoColumn className="h-full w-full md:min-h-[500px]">
        <TwoColumn.Left className="relative min-h-[180px] w-full md:h-full">
          <BackgroundImage backgroundImage={getRealUrl(Image.url)} />
        </TwoColumn.Left>

        <TwoColumn.Right className="h-full w-full">
          <div
            className={`${formScope} ${isHom ? '' : 'bg-bg-neutrals-0'} m-4 flex flex-col gap-6 md:m-7 lg:m-14 lg:max-w-[calc(var(--wrapper-size)/2)] xl:w-[548px] ${
              FormStyle === 'boxed'
                ? 'rounded-custom bg-primary-600 text-neutrals-0 p-8'
                : 'h-full'
            }`}
          >
            {header}
            {Descripcion && (
              <div
                // Los enlaces inline del richtext heredan el tamaño del texto
                // que los envuelve; sin esto el reset global `a { text-link }`
                // (13px) los deja mucho más pequeños que el párrafo.
                // En estilo hom los enlaces del parrafo van en bold y con el
                // color del texto, no en teal.
                className={oneLine`[&_a]:[font-size:inherit] [&_a]:[line-height:inherit] ${
                  isHom ? '[&_a]:font-bold [&_a]:[color:inherit]' : ''
                } ${descripcionCls ?? ''}`}
                dangerouslySetInnerHTML={{ __html: Descripcion }}
              />
            )}
            <ContactForm
              FormStyle={FormStyle}
              destinatario={Destinatario?.documentId}
            />
          </div>
        </TwoColumn.Right>
      </TwoColumn>
    </section>
  ) : (
    <Container wrapper {...sectionBgProps(FondoSeccion)}>
      <TwoColumn className="h-full w-full md:min-h-[500px]">
        <TwoColumn.Left className="relative min-h-[180px] w-full md:h-full">
          <div className="flex h-full w-full flex-col items-center justify-center gap-8 p-4">
            {headerNoImage}
            {Descripcion && (
              <div
                // Los enlaces inline del richtext heredan el tamaño del texto
                // que los envuelve; sin esto el reset global `a { text-link }`
                // (13px) los deja mucho más pequeños que el párrafo.
                // En estilo hom los enlaces del parrafo van en bold y con el
                // color del texto, no en teal.
                className={oneLine`[&_a]:[font-size:inherit] [&_a]:[line-height:inherit] ${
                  isHom ? '[&_a]:font-bold [&_a]:[color:inherit]' : ''
                } ${descripcionCls ?? ''}`}
                dangerouslySetInnerHTML={{ __html: Descripcion }}
              />
            )}
          </div>
        </TwoColumn.Left>

        <TwoColumn.Right className="h-full w-full">
          <div
            className={`${formScope} w-full ${
              FormStyle === 'boxed'
                ? 'rounded-custom bg-primary-600 text-neutrals-0 p-8'
                : 'h-full'
            }`}
          >
            <ContactForm
              FormStyle={FormStyle}
              destinatario={Destinatario?.documentId}
            />
          </div>
        </TwoColumn.Right>
      </TwoColumn>
    </Container>
  );
};

ComponentRevisadasContactForm.displayName = 'ComponentRevisadasContactForm';
